import { useMemo } from "react";

import Alert from "common/components/ui/Alert";
import { Button } from "common/components/ui/Button";
import {
  DialogBody,
  DialogFooter,
  DialogHeader,
} from "common/components/ui/Dialog";
import {
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import useLicenseMutations from "common/datahooks/useLicenseMutations";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { WorkspaceAccount } from "common/types";

import { startAccountsBulkAction } from "../../utils";

interface AssignSeatsProps {
  selectedAccounts: WorkspaceAccount[];
  onBack: () => void;
  onSuccess: () => void;
}

export default function AssignSeats({
  selectedAccounts,
  onBack,
  onSuccess,
}: AssignSeatsProps) {
  const isDesktop = useTwBreakpoint("lg");

  const Footer = isDesktop ? DialogFooter : DrawerFooter;

  const _Body = isDesktop ? DialogBody : DrawerBody;

  const Header = isDesktop ? DialogHeader : DrawerHeader;
  const { assignLicense, isAssigningLicense } = useLicenseMutations();

  const rentedAccounts = useMemo(
    () => selectedAccounts.filter(({ type }) => type === "rental"),
    [selectedAccounts],
  );

  const { accountsWithSeats, accountsWithoutSeats } = useMemo(() => {
    const withSeats: WorkspaceAccount[] = [];
    const withoutSeats: WorkspaceAccount[] = [];

    selectedAccounts.forEach((account) => {
      if (account.license || account.type === "rental") {
        withSeats.push(account);
      } else {
        withoutSeats.push(account);
      }
    });

    return { accountsWithSeats: withSeats, accountsWithoutSeats: withoutSeats };
  }, [selectedAccounts]);

  function assignSeats() {
    startAccountsBulkAction(
      (account, abortSignal) =>
        assignLicense({ accountId: account.id, abortSignal }),
      accountsWithoutSeats,
      (finishedItemsCount) =>
        finishedItemsCount === accountsWithoutSeats.length
          ? `Assignment complete`
          : `Assigning ${finishedItemsCount}/${accountsWithoutSeats.length} seats`,
      true,
    );
    onSuccess();
  }

  const title = "Assign seats";
  let description: string;

  if (accountsWithoutSeats.length === 0) {
    description = "All selected LinkedIn accounts already have seats assigned";
  } else if (accountsWithSeats.length > 0) {
    const withSeatsText =
      accountsWithSeats.length === 1
        ? `${accountsWithSeats[0].full_name} already has seat assigned`
        : `${accountsWithSeats.length} accounts already have seats assigned`;

    const withoutSeatsText =
      accountsWithoutSeats.length === 1
        ? `a seat to ${accountsWithoutSeats[0].full_name}`
        : `seats to the remaining ${accountsWithoutSeats.length} accounts`;

    description = `${withSeatsText}, do you want to assign ${withoutSeatsText}?`;
  } else {
    description =
      selectedAccounts.length === 1
        ? `Assign a seat to ${selectedAccounts[0].full_name}?`
        : `Assign seats to ${selectedAccounts.length} accounts?`;
  }

  return (
    <>
      <Header title={title} subtitle={description} onBack={onBack} />

      {rentedAccounts.length > 0 && (
        <Alert variant="warning" className="my-4">
          Rented LinkedIn accounts always have seats
        </Alert>
      )}

      <Footer className="justify-end">
        <Button variant="secondary-black" onClick={onBack}>
          Cancel
        </Button>
        <Button
          variant="gradient"
          onClick={assignSeats}
          isLoading={isAssigningLicense}
          disabled={!accountsWithoutSeats.length}
        >
          Assign
        </Button>
      </Footer>
    </>
  );
}
